import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';

/**
 * Modern 404 Not Found page with illustration
 */
const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error('404 Error: User attempted to access non-existent route:', location.pathname);
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-background to-muted/30 px-4">
      <div className="w-full max-w-md text-center">
        {/* Illustration */}
        <div className="mb-8">
          <svg
            className="mx-auto h-64 w-64 text-muted-foreground/20"
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Background circle */}
            <circle cx="100" cy="100" r="80" fill="currentColor" opacity="0.3" />

            {/* Magnifying glass */}
            <circle
              cx="85"
              cy="85"
              r="35"
              stroke="currentColor"
              strokeWidth="8"
              fill="none"
              opacity="0.6"
            />
            <line
              x1="110"
              y1="110"
              x2="145"
              y2="145"
              stroke="currentColor"
              strokeWidth="10"
              strokeLinecap="round"
              opacity="0.6"
            />

            {/* Question mark inside magnifying glass */}
            <text
              x="85"
              y="95"
              textAnchor="middle"
              fontSize="40"
              fontWeight="bold"
              fill="currentColor"
              opacity="0.5"
            >
              ?
            </text>

            {/* Decorative dots */}
            <circle cx="160" cy="50" r="5" fill="currentColor" opacity="0.4" />
            <circle cx="45" cy="150" r="4" fill="currentColor" opacity="0.3" />
            <circle cx="170" cy="130" r="3" fill="currentColor" opacity="0.4" />
            <circle cx="30" cy="70" r="6" fill="currentColor" opacity="0.3" />
          </svg>
        </div>

        {/* 404 Text */}
        <h1 className="mb-2 text-8xl font-bold text-primary/80">404</h1>

        {/* Message */}
        <h2 className="mb-3 text-2xl font-semibold text-foreground">Page not found</h2>
        <p className="mb-8 text-muted-foreground">
          The page you're looking for doesn't exist or has been moved. Let's get you back on track.
        </p>

        {/* Action buttons */}
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button
            onClick={() => navigate(ROUTES.MASTERCLASS_HOME)}
            className="bg-primary hover:bg-primary/90"
          >
            Go to Masterclasses
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="border-muted-foreground/30"
          >
            Go Back
          </Button>
        </div>

        {/* Attempted path (for debugging) */}
        <p className="mt-8 text-xs text-muted-foreground/60">
          Attempted path:{' '}
          <code className="rounded bg-muted px-1.5 py-0.5">{location.pathname}</code>
        </p>
      </div>
    </div>
  );
};

export default NotFound;
